/** Formata um CNPJ (ou o alfanumérico equivalente da Receita) durante a digitação. */
export function formatCNPJInput(value: string): string {
  const cleaned = value.replace(/[.\-/]/g, '').toUpperCase();
  const limited = cleaned.slice(0, 14);

  if (limited.length <= 2) {
    return limited;
  } else if (limited.length <= 5) {
    return `${limited.slice(0, 2)}.${limited.slice(2)}`;
  } else if (limited.length <= 8) {
    return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5)}`;
  } else if (limited.length <= 12) {
    return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5, 8)}/${limited.slice(8)}`;
  }
  return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5, 8)}/${limited.slice(8, 12)}-${limited.slice(12, 14)}`;
}

/** Formata um telefone brasileiro durante a digitação: (XX) XXXX-XXXX ou (XX) XXXXX-XXXX. */
export function formatPhoneInput(value: string): string {
  const cleaned = value.replace(/\D/g, '');
  const limited = cleaned.slice(0, 11);

  if (limited.length === 0) {
    return '';
  } else if (limited.length <= 2) {
    return `(${limited}`;
  } else if (limited.length <= 6) {
    return `(${limited.slice(0, 2)}) ${limited.slice(2)}`;
  } else if (limited.length <= 10) {
    return `(${limited.slice(0, 2)}) ${limited.slice(2, 6)}-${limited.slice(6)}`;
  }
  return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7, 11)}`;
}

export interface AvailablePackage {
  key: string;
  name?: string;
  description?: string;
  enabled?: boolean;
  icon?: string;
  basePrice?: number;
  includedUsers?: number;
  extraUserPricing?: unknown;
  clientCapacityPacks?: Array<{ capacity: number; price: number }>;
  nucleusCapacityPacks?: Array<{ capacity: number; price: number }>;
  condoCapacityPacks?: Array<{ capacity: number; price: number }>;
}

export interface DisplayPricing {
  basePrice: number;
  includedUsers: number;
  extraUserPricing: unknown;
  clientCapacityPacks: Array<{ capacity: number; price: number }>;
  nucleusCapacityPacks: Array<{ capacity: number; price: number }>;
}

/**
 * Resolve a config de preços efetiva para exibição, a partir do pacote escolhido: procura em
 * `availablePackages` (formato atual), cai para `pricingConfig.packages` (legado) e por fim usa a
 * config global do `pricingConfig`. `basePrice`/`includedUsers`/`extraUserPricing` só se aplicam
 * ao pacote institucional (os demais pacotes não têm mensalidade fixa por usuário).
 */
interface DisplayPricingConfig {
  basePrice?: number;
  includedUsers?: number;
  extraUserPricing?: unknown;
  packages?: Record<string, {
    basePrice?: number;
    includedUsers?: number;
    extraUserPricing?: unknown;
    nucleusCapacityPacks?: Array<{ capacity: number; price: number }>;
    clientCapacityPacks?: Array<{ capacity: number; price: number }>;
    condoCapacityPacks?: Array<{ capacity: number; price: number }>;
  }>;
}

export function computeDisplayPricing(
  pricingConfig: DisplayPricingConfig | null | undefined,
  packageType: 'condominio' | 'gestao' | 'institucional',
  availablePackages: AvailablePackage[],
): DisplayPricing | null {
  if (!pricingConfig) return null;

  const pkgFromAvailable = availablePackages.find((p) => {
    const k = (p.key || '').toLowerCase();
    return k === packageType || (packageType === 'gestao' && k === 'operador');
  });
  const nucleusPacksFromAvailable = pkgFromAvailable?.nucleusCapacityPacks ?? pkgFromAvailable?.clientCapacityPacks ?? [];
  const clientPacksFromAvailable = pkgFromAvailable?.clientCapacityPacks ?? pkgFromAvailable?.condoCapacityPacks ?? [];
  if (pkgFromAvailable) {
    return {
      basePrice: packageType === 'institucional' ? (pkgFromAvailable.basePrice ?? pricingConfig.basePrice ?? 99) : 0,
      includedUsers:
        packageType === 'institucional' ? (pkgFromAvailable.includedUsers ?? pricingConfig.includedUsers ?? 3) : 0,
      extraUserPricing:
        packageType === 'institucional' ? (pkgFromAvailable.extraUserPricing ?? pricingConfig.extraUserPricing) : undefined,
      clientCapacityPacks: packageType === 'institucional' ? nucleusPacksFromAvailable : clientPacksFromAvailable,
      nucleusCapacityPacks: nucleusPacksFromAvailable,
    };
  }

  const packages = pricingConfig.packages;
  const pkgFromPackages = packages?.[packageType] ?? (packageType === 'gestao' ? packages?.operador : undefined);
  if (pkgFromPackages) {
    const p = pkgFromPackages;
    const np = p.nucleusCapacityPacks ?? [];
    const cp = p.clientCapacityPacks ?? p.condoCapacityPacks ?? [];
    return {
      basePrice: packageType === 'institucional' ? (p.basePrice ?? pricingConfig.basePrice ?? 99) : 0,
      includedUsers: packageType === 'institucional' ? (p.includedUsers ?? pricingConfig.includedUsers ?? 3) : 0,
      extraUserPricing: packageType === 'institucional' ? (p.extraUserPricing ?? pricingConfig.extraUserPricing) : undefined,
      clientCapacityPacks: packageType === 'institucional' ? np : cp,
      nucleusCapacityPacks: np,
    };
  }

  return {
    basePrice: packageType === 'institucional' ? (pricingConfig.basePrice ?? 99) : 0,
    includedUsers: packageType === 'institucional' ? (pricingConfig.includedUsers ?? 3) : 0,
    extraUserPricing: packageType === 'institucional' ? pricingConfig.extraUserPricing : undefined,
    clientCapacityPacks: [],
    nucleusCapacityPacks: [],
  };
}
